import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Trophy, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CustomAchievement, CustomAchievementInput } from '@/hooks/useCustomAchievements';

interface AchievementFormProps {
  achievement?: CustomAchievement | null;
  onSubmit: (data: CustomAchievementInput) => Promise<{ success: boolean; error?: string }>;
  onClose: () => void;
}

const BADGE_OPTIONS = ['🏆', '🌟', '💎', '🔥', '⚡', '🎯', '🚀', '💪', '🎖️', '🏅', '👑', '✨', '💫', '🌈', '🎁', '💰', '🎓', '🔮', '🦄', '🐉'];

const CATEGORY_OPTIONS = [
  { value: 'ventas', label: 'Ventas', icon: '🛒' },
  { value: 'canjes', label: 'Canjes', icon: '🎁' },
  { value: 'ahorros', label: 'Ahorros', icon: '💰' },
  { value: 'entrenamiento', label: 'Entrenamiento', icon: '🎓' },
  { value: 'ranking', label: 'Ranking', icon: '🏆' },
  { value: 'especial', label: 'Especiales', icon: '✨' },
];

export const AchievementForm: React.FC<AchievementFormProps> = ({
  achievement,
  onSubmit,
  onClose,
}) => {
  const isEditing = !!achievement;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CustomAchievementInput>({
    type: '',
    badge: '🏆',
    name: '',
    description: '',
    requirement: '',
    points: 10,
    category: 'especial',
    is_permanent: false,
    is_active: true,
  });

  useEffect(() => {
    if (achievement) {
      setFormData({
        type: achievement.type,
        badge: achievement.badge,
        name: achievement.name,
        description: achievement.description,
        requirement: achievement.requirement,
        points: achievement.points,
        category: achievement.category,
        is_permanent: achievement.is_permanent,
        is_active: achievement.is_active,
      });
    }
  }, [achievement]);

  const generateType = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      type: isEditing ? prev.type : `custom_${generateType(name)}`,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim() || !formData.description.trim() || !formData.requirement.trim()) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }

    if (formData.points < 1) {
      setError('Los puntos deben ser al menos 1');
      return;
    }

    setLoading(true);
    const result = await onSubmit(formData);
    setLoading(false);

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Error al guardar');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-white">
              {isEditing ? 'Editar Logro' : 'Nuevo Logro'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Badge selector */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Badge / Emoji</Label>
            <div className="flex flex-wrap gap-2">
              {BADGE_OPTIONS.map((badge) => (
                <button
                  key={badge}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, badge }))}
                  className={`w-10 h-10 text-xl rounded-lg transition-all ${
                    formData.badge === badge
                      ? 'bg-purple-500/30 border-2 border-purple-500 scale-110'
                      : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  {badge}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300">Nombre del Logro *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ej: Super Vendedor"
              className="bg-zinc-800 border-zinc-700"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-zinc-300">Descripción *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Ej: Logra 20 ventas en una semana"
              rows={2}
              className="bg-zinc-800 border-zinc-700 resize-none"
            />
          </div>

          {/* Requirement */}
          <div className="space-y-2">
            <Label htmlFor="requirement" className="text-zinc-300">Cómo Obtenerlo *</Label>
            <Textarea
              id="requirement"
              value={formData.requirement}
              onChange={(e) => setFormData(prev => ({ ...prev, requirement: e.target.value }))}
              placeholder="Ej: El administrador lo asigna manualmente a vendedores destacados"
              rows={2}
              className="bg-zinc-800 border-zinc-700 resize-none"
            />
          </div>

          {/* Points and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="points" className="text-zinc-300">Puntos</Label>
              <Input
                id="points"
                type="number"
                min={1}
                max={500}
                value={formData.points}
                onChange={(e) => setFormData(prev => ({ ...prev, points: parseInt(e.target.value) || 10 }))}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Categoría</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Switches */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-white">Permanente</p>
                <p className="text-xs text-zinc-400">No se reinicia cada mes</p>
              </div>
              <Switch
                checked={formData.is_permanent}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_permanent: checked }))}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-white">Activo</p>
                <p className="text-xs text-zinc-400">Visible para vendedores</p>
              </div>
              <Switch
                checked={formData.is_active ?? true}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          {/* Preview */}
          <div className="pt-2">
            <Label className="text-zinc-300 mb-2 block">Vista Previa</Label>
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-3xl shadow-lg">
                {formData.badge}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-white truncate">{formData.name || 'Nombre del logro'}</h4>
                <p className="text-sm text-white/60 truncate">{formData.description || 'Descripción'}</p>
                <p className="text-xs text-purple-400 mt-1">+{formData.points} pts</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Guardar Cambios' : 'Crear Logro'}
                </>
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AchievementForm;
